// Template Marketplace (WF-20) — browse KaziPeople-verified evaluation frameworks,
// install one (copy-on-install: a deep copy lands in your library, no employee data
// travels), and list one of your OWN published frameworks for others to use. Listing
// goes to a platform operator for verification before it appears here.
//
// docs/workforce-framework/template-marketplace.md.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { Download, FileUp, Loader2, Star, Store, Tag, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useHasPermission } from '@/rbac/useRbac';
import {
  browseMarketplace, installListing, isNotRolledOut, listFrameworks, submitListing,
  type Framework, type Listing,
} from '@/services/workforceService';
import { ImportFrameworkDialog } from './ImportFrameworkDialog';

const inputCls =
  'w-full h-9 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground';

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

const priceLabel = (l: Listing): string => {
  if (!l.license_type || l.license_type === 'free') return 'Free';
  if (l.price_amount == null) return l.license_type;
  const amt = `${l.price_currency || ''} ${l.price_amount}`.trim();
  if (l.license_type === 'subscription') return `${amt}/${l.billing_period || 'mo'}`;
  return amt;
};

export const MarketplaceTab: React.FC = () => {
  const { t } = useTranslation('workforce');
  const canPublish = useHasPermission('workforce:template:publish');
  const canInstall = useHasPermission('workforce:framework:write');
  const confirm = useConfirm();

  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [notRolledOut, setNotRolledOut] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [listDlg, setListDlg] = useState(false);
  const [importDlg, setImportDlg] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setListings(await browseMarketplace());
    } catch (e) {
      if (isNotRolledOut(e)) setNotRolledOut(true);
      else toast.error(getApiErrorMessage(e, errMsg(e, 'Could not load the marketplace')));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const onInstall = async (l: Listing) => {
    const ok = await confirm({
      title: t('mkt_install_title', { defaultValue: 'Install this template?' }),
      message: t('mkt_install_body', {
        defaultValue:
          'A private copy is added to your frameworks. You can edit it freely — it never links back to the original, and no data is shared either way.',
      }),
      confirmLabel: t('mkt_install', { defaultValue: 'Install' }),
    });
    if (!ok) return;
    setBusy(l.id);
    try {
      await installListing(l.id);
      toast.success(t('mkt_installed', { defaultValue: 'Installed to your frameworks.' }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, errMsg(e, 'Install failed')));
    } finally {
      setBusy(null);
    }
  };

  if (notRolledOut) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          {t('mkt_not_ready', {
            defaultValue: 'The workforce engine is not available in this workspace yet.',
          })}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Store className="w-4 h-4" />
          {t('mkt_intro', {
            defaultValue:
              'Verified evaluation frameworks you can install into your library. Installing makes a private copy — nothing links back.',
          })}
        </div>
        <div className="flex items-center gap-2">
          {canInstall && (
            <Button variant="outline" size="sm" onClick={() => setImportDlg(true)}>
              <FileUp className="w-4 h-4 mr-1.5" />
              {t('mkt_import_cta', { defaultValue: 'Import framework' })}
            </Button>
          )}
          {canPublish && (
            <Button variant="outline" size="sm" onClick={() => setListDlg(true)}>
              <Upload className="w-4 h-4 mr-1.5" />
              {t('mkt_list_cta', { defaultValue: 'List a framework' })}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
        </div>
      ) : listings.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {t('mkt_empty', {
              defaultValue: 'No templates are listed yet. Be the first — list one of yours.',
            })}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((l) => (
            <Card key={l.id} className="flex flex-col">
              <CardContent className="py-4 flex flex-col gap-2 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-medium text-foreground">{l.title}</h3>
                  {l.featured && <Badge variant="secondary">★</Badge>}
                </div>
                {l.summary && (
                  <p className="text-sm text-muted-foreground line-clamp-3">{l.summary}</p>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {l.category && (
                    <Badge variant="outline">
                      <Tag className="w-3 h-3 mr-1" />
                      {l.category}
                    </Badge>
                  )}
                  {l.rating_count > 0 && (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-current text-amber-500" />
                      {l.rating_avg.toFixed(1)} ({l.rating_count})
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-auto pt-2">
                  <Badge variant={priceLabel(l) === 'Free' ? 'secondary' : 'default'}>
                    {priceLabel(l)}
                  </Badge>
                  {canInstall && (
                    <Button size="sm" disabled={busy === l.id} onClick={() => onInstall(l)}>
                      {busy === l.id ? (
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-1.5" />
                      )}
                      {t('mkt_install', { defaultValue: 'Install' })}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {listDlg && <ListDialog onClose={() => setListDlg(false)} onDone={load} />}
      {importDlg && <ImportFrameworkDialog onClose={() => setImportDlg(false)} onDone={load} />}
    </div>
  );
};

// ---------------------------------------------------------------- list dialog
const ListDialog: React.FC<{ onClose: () => void; onDone: () => void }> = ({ onClose, onDone }) => {
  const { t } = useTranslation('workforce');
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('');
  const [licenseType, setLicenseType] = useState('free');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const fws = await listFrameworks();
        setFrameworks(fws.filter((f) => f.status === 'active' || f.status === 'published'));
      } catch {
        /* empty */
      }
    })();
  }, []);

  const submit = async () => {
    if (!frameworkId || !title) {
      toast.error(t('mkt_need_fields', { defaultValue: 'Pick a published framework and give it a title.' }));
      return;
    }
    setSaving(true);
    try {
      await submitListing({
        framework_id: frameworkId,
        title,
        summary,
        category,
        license_type: licenseType,
        ...(licenseType !== 'free' && price ? { price_amount: Number(price), price_currency: 'TZS' } : {}),
      });
      toast.success(
        t('mkt_submitted', {
          defaultValue: 'Submitted for review. A KaziPeople operator verifies it before it goes live.',
        }),
      );
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, 'Could not submit the listing')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('mkt_list_title', { defaultValue: 'List a framework' })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">
              {t('mkt_framework', { defaultValue: 'Published framework' })}
            </label>
            <select className={inputCls} value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)}>
              <option value="">—</option>
              {frameworks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <input
            className={inputCls}
            placeholder={t('mkt_title_ph', { defaultValue: 'Listing title' })}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            className={inputCls + ' h-20 py-2'}
            placeholder={t('mkt_summary_ph', { defaultValue: 'Short summary' })}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              className={inputCls}
              placeholder={t('mkt_category_ph', { defaultValue: 'Category (e.g. NGO)' })}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
            <select className={inputCls} value={licenseType} onChange={(e) => setLicenseType(e.target.value)}>
              <option value="free">{t('mkt_free', { defaultValue: 'Free' })}</option>
              <option value="one_time">{t('mkt_onetime', { defaultValue: 'One-time' })}</option>
              <option value="subscription">{t('mkt_sub', { defaultValue: 'Subscription' })}</option>
            </select>
          </div>
          {licenseType !== 'free' && (
            <input
              className={inputCls}
              type="number"
              placeholder={t('mkt_price_ph', { defaultValue: 'Price (TZS)' })}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {t('mkt_submit', { defaultValue: 'Submit for review' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarketplaceTab;
